
import { useState } from 'react';

export default function Module1() {
  const [activeSection, setActiveSection] = useState<number | null>(null);

  const sections = [
    {
      id: 1,
      title: "外贸全流程概述",
      subtitle: "一张图看懂外贸",
      icon: "ri-flow-chart",
      content: {
        description: "通过清晰的流程图，将外贸流程分为交易前、交易中、交易后三大阶段",
        details: [
          {
            phase: "交易前",
            color: "from-blue-500 to-blue-600",
            steps: [
              "市场调研 - 了解目标市场需求和竞争状况",
              "客户开发 - 通过展会、B2B平台、社媒等渠道",
              "询盘报价 - 接收客户询盘并提供专业报价"
            ]
          },
          {
            phase: "交易中", 
            color: "from-green-500 to-green-600",
            steps: [
              "签订合同 - 确认PI（形式发票）或CI（商业发票）",
              "客户付款 - 收取定金确保交易安全",
              "生产备货 - 按合同要求组织生产",
              "订舱装货 - 安排海运舱位并装箱",
              "报关报检 - 完成海关申报和商检手续"
            ]
          },
          {
            phase: "交易后",
            color: "from-purple-500 to-purple-600", 
            steps: [
              "获取提单 - 取得货物运输凭证",
              "发放单证 - 向客户提供全套单据",
              "收取尾款 - 完成最终货款结算",
              "办理退税 - （一般贸易）申请出口退税",
              "售后维护 - 维护客户关系，为后续合作奠定基础"
            ]
          }
        ]
      }
    },
    {
      id: 2,
      title: "核心贸易术语解析",
      subtitle: "风险与费用的关键",
      icon: "ri-ship-line",
      content: {
        description: "重点讲解FOB、CIF、CFR三个最常用贸易术语的风险与费用划分",
        terms: [
          {
            term: "FOB",
            fullName: "Free On Board（装运港船上交货）",
            color: "from-red-500 to-red-600",
            description: "卖家承担货到装运港码头的一切费用和风险，责任止于吊钩",
            responsibilities: {
              seller: ["货物运至装运港", "装船前的所有费用", "出口报关手续", "装船费用"],
              buyer: ["海运费", "海运保险费", "目的港费用", "进口报关费用"]
            },
            risk: "货物越过船舷后风险转移给买方"
          },
          {
            term: "CIF",
            fullName: "Cost, Insurance and Freight（成本、保险费加运费）",
            color: "from-blue-500 to-blue-600", 
            description: "卖家承担货到目的港的海运费和保险费，但风险仍在装运港转移",
            responsibilities: {
              seller: ["货物运至装运港", "海运费", "海运保险费", "出口报关费用"],
              buyer: ["目的港费用", "进口报关费用", "货物到港后的费用"]
            },
            risk: "货物上船后损坏，由买家找保险公司索赔"
          },
          {
            term: "CFR", 
            fullName: "Cost and Freight（成本加运费）",
            color: "from-green-500 to-green-600",
            description: "等同于CIF但不包含保险，买方需自行购买保险",
            responsibilities: {
              seller: ["货物运至装运港", "海运费", "出口报关费用"],
              buyer: ["海运保险费", "目的港费用", "进口报关费用"]
            },
            risk: "风险转移点与FOB相同，但运费由卖方承担"
          }
        ]
      }
    },
    {
      id: 3,
      title: "关键单证简介",
      subtitle: "外贸的语言",
      icon: "ri-file-text-line",
      content: {
        description: "详解外贸业务中的核心单证及其作用",
        documents: [
          {
            name: "商业发票",
            englishName: "Commercial Invoice",
            icon: "ri-bill-line",
            color: "from-orange-500 to-orange-600",
            description: "外贸交易的核心单据，用于报关、付款、记账",
            contents: ["买卖双方完整信息", "货物品名及规格", "单价和总价", "贸易术语", "付款条件"],
            importance: "所有其他单证都以发票为基准制作"
          },
          {
            name: "装箱单",
            englishName: "Packing List", 
            icon: "ri-archive-line",
            color: "from-teal-500 to-teal-600",
            description: "商业发票的补充，详述包装细节",
            contents: ["总箱数", "毛重/净重", "包装箱规格尺寸", "每箱内容明细"],
            importance: "海关查验和客户收货的重要依据"
          },
          {
            name: "提单",
            englishName: "Bill of Lading (B/L)",
            icon: "ri-ship-2-line", 
            color: "from-blue-500 to-blue-600",
            description: "货物运输和物权转移的核心凭证",
            contents: ["正本提单 - 物权凭证，需要寄送", "电放提单 - 电子放货，快捷但有风险"],
            importance: "控制货权的关键单证，谁持有正本提单谁就拥有货物"
          },
          {
            name: "原产地证",
            englishName: "Certificate of Origin",
            icon: "ri-medal-line",
            color: "from-purple-500 to-purple-600", 
            description: "享受关税优惠的\"护照\"",
            contents: ["COO - 一般原产地证", "Form A - 普惠制原产地证", "区域性优惠原产地证"],
            importance: "帮助客户在目的国享受关税减免优惠"
          },
          {
            name: "报关单",
            englishName: "Customs Declaration",
            icon: "ri-government-line",
            color: "from-red-500 to-red-600",
            description: "向海关申报的法律文件",
            contents: ["货物详细信息", "贸易方式", "征免性质", "运输方式"],
            importance: "合法出口的必要文件，退税申报的重要依据"
          }
        ]
      }
    },
    {
      id: 4,
      title: "付款方式与风险",
      subtitle: "资金安全的保障",
      icon: "ri-secure-payment-line",
      content: {
        description: "详解常用付款方式的流程、成本和风险对比",
        methods: [
          {
            name: "T/T电汇",
            englishName: "Telegraphic Transfer",
            icon: "ri-bank-line",
            color: "from-green-500 to-green-600",
            popularity: "最常用",
            process: [
              "买方向银行申请电汇", 
              "银行扣除手续费后汇出",
              "卖方银行收到款项并入账",
              "常见比例：30%定金 + 70%见提单副本"
            ],
            advantages: ["操作简单", "费用较低", "到账迅速"],
            risks: ["100%前T/T：买方风险大", "100%后T/T：卖方风险大"],
            recommendation: "建议采用分期付款方式平衡风险"
          },
          {
            name: "L/C信用证",
            englishName: "Letter of Credit", 
            icon: "ri-bank-card-line",
            color: "from-blue-500 to-blue-600",
            popularity: "银行信用",
            process: [
              "买方向银行申请开证",
              "开证行开立信用证",
              "通知行通知卖方",
              "卖方发货并交单",
              "银行审单后付款"
            ],
            advantages: ["相对安全", "银行信用担保", "适合大额交易"],
            risks: ["费用高", "流程复杂", "容易产生不符点", "单证一致要求严格"],
            recommendation: "适合初次合作或大额订单，务必确保单证一致"
          },
          {
            name: "D/P付款交单",
            englishName: "Documents against Payment",
            icon: "ri-exchange-line", 
            color: "from-orange-500 to-orange-600",
            popularity: "中等风险",
            process: [
              "卖方发货后向银行交单",
              "银行将单据寄送买方银行",
              "买方付款后银行交付单据",
              "买方凭单据提货"
            ],
            advantages: ["费用适中", "操作相对简单", "银行作为中间人"],
            risks: ["买方拒付时货物滞留", "需要信任买方资信"],
            recommendation: "适合有一定合作基础的客户，风险介于T/T和L/C之间"
          }
        ]
      }
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                <i className="ri-graduation-cap-line text-white text-xl"></i>
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: "Pacifico, serif" }}>天健商业广场外贸培训学院</h1>
                <p className="text-sm text-gray-600">专业外贸知识培训平台</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <button 
                onClick={() => window.REACT_APP_NAVIGATE('/')}
                className="px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-arrow-left-line mr-2"></i>
                返回首页
              </button>
              <button className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer">
                立即报名
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('https://public.readdy.ai/ai/img_res/e108d5f539fba5a190674e52b1c29266.jpg')`
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-blue-900/90 to-purple-900/70"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-6 py-16">
          <div className="max-w-4xl">
            <div className="flex items-center space-x-3 mb-4">
              <span className="bg-blue-500 text-white px-4 py-2 rounded-full text-sm font-medium">模块一</span>
              <span className="bg-white/20 text-white px-4 py-2 rounded-full text-sm font-medium">1.5小时</span>
            </div>
            <h1 className="text-5xl font-bold text-white mb-4 leading-tight">
              外贸基础入门
            </h1>
            <p className="text-xl text-blue-100 mb-6">
              夯实基础 · 让零基础学员建立对外贸全流程的宏观概念，理解关键术语和单证的核心作用
            </p>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 mb-8">
              <h3 className="text-lg font-semibold text-white mb-3">培训目标：</h3>
              <p className="text-blue-100 leading-relaxed">
                通过本模块学习，学员将全面了解外贸业务的完整流程，掌握核心贸易术语的实际应用，
                熟悉关键单证的制作要点，并能够根据不同情况选择合适的付款方式。
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Course Content */}
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">详细内容</h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            四大核心主题，全面覆盖外贸基础知识体系
          </p>
        </div>

        {/* Content Sections */}
        <div className="space-y-8">
          {sections.map((section, index) => (
            <div key={section.id} className="bg-white rounded-2xl shadow-lg overflow-hidden">
              <div 
                className="bg-gradient-to-r from-gray-600 to-gray-700 p-6 cursor-pointer"
                onClick={() => setActiveSection(activeSection === section.id ? null : section.id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                      <i className={`${section.icon} text-white text-xl`}></i>
                    </div>
                    <div>
                      <div className="flex items-center space-x-3 mb-2">
                        <span className="bg-white/20 text-white px-3 py-1 rounded-full text-sm font-medium">
                          主题{index + 1}
                        </span>
                      </div>
                      <h3 className="text-2xl font-bold text-white mb-1">{section.title}</h3>
                      <p className="text-white/80 text-lg">{section.subtitle}</p>
                    </div>
                  </div>
                  <div className="text-white">
                    <i className={`${activeSection === section.id ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-2xl`}></i>
                  </div>
                </div>
              </div>
              
              {activeSection === section.id && (
                <div className="p-8">
                  {/* Section 1: 外贸全流程概述 */}
                  {section.id === 1 && (
                    <div>
                      <p className="text-lg text-gray-700 mb-8">{section.content.description}</p>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {section.content.details.map((phase, phaseIndex) => (
                          <div key={phaseIndex} className="relative">
                            <div className={`bg-gradient-to-r ${phase.color} rounded-2xl p-6 text-white h-full`}>
                              <h4 className="text-xl font-bold mb-4 flex items-center">
                                <span className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center text-sm font-bold mr-3">
                                  {phaseIndex + 1}
                                </span>
                                {phase.phase}
                              </h4>
                              <div className="space-y-3">
                                {phase.steps.map((step, stepIndex) => (
                                  <div key={stepIndex} className="flex items-start space-x-3">
                                    <div className="w-2 h-2 bg-white/60 rounded-full mt-2 flex-shrink-0"></div>
                                    <p className="text-white/90 text-sm leading-relaxed">{step}</p>
                                  </div>
                                ))}
                              </div>
                            </div>
                            {phaseIndex < section.content.details.length - 1 && (
                              <div className="hidden md:block absolute top-1/2 -right-3 transform -translate-y-1/2 z-10">
                                <div className="w-6 h-6 bg-gray-300 rounded-full flex items-center justify-center">
                                  <i className="ri-arrow-right-line text-gray-600 text-sm"></i>
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                      <div className="mt-8 bg-blue-50 rounded-lg p-6">
                        <h5 className="font-semibold text-blue-900 mb-3">
                          <i className="ri-question-line mr-2"></i>
                          互动提问
                        </h5>
                        <p className="text-blue-800">
                          请思考：您的公司目前业务主要处于哪个环节？是否存在可以优化的地方？
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Section 2: 核心贸易术语解析 */}
                  {section.id === 2 && (
                    <div>
                      <p className="text-lg text-gray-700 mb-8">{section.content.description}</p>
                      <div className="space-y-6">
                        {section.content.terms.map((term, termIndex) => (
                          <div key={termIndex} className={`bg-gradient-to-r ${term.color} rounded-2xl p-6 text-white`}>
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                              <div>
                                <h4 className="text-3xl font-bold mb-2">{term.term}</h4>
                                <p className="text-white/80 text-lg mb-4">{term.fullName}</p>
                                <p className="text-white/90 mb-6">{term.description}</p>
                                <div className="bg-white/10 rounded-lg p-4">
                                  <h5 className="font-semibold mb-2">
                                    <i className="ri-alert-line mr-2"></i>
                                    风险转移点
                                  </h5>
                                  <p className="text-white/90 text-sm">{term.risk}</p>
                                </div>
                              </div>
                              <div className="space-y-4">
                                <div className="bg-white/10 rounded-lg p-4">
                                  <h5 className="font-semibold mb-3">
                                    <i className="ri-user-line mr-2"></i>
                                    卖方责任
                                  </h5>
                                  <ul className="space-y-2">
                                    {term.responsibilities.seller.map((resp, idx) => (
                                      <li key={idx} className="flex items-center text-sm">
                                        <i className="ri-check-line mr-2 text-green-300"></i>
                                        {resp}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                                <div className="bg-white/10 rounded-lg p-4">
                                  <h5 className="font-semibold mb-3">
                                    <i className="ri-user-2-line mr-2"></i>
                                    买方责任
                                  </h5>
                                  <ul className="space-y-2">
                                    {term.responsibilities.buyer.map((resp, idx) => (
                                      <li key={idx} className="flex items-center text-sm">
                                        <i className="ri-check-line mr-2 text-blue-300"></i>
                                        {resp}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="mt-8 bg-yellow-50 rounded-lg p-6">
                        <h5 className="font-semibold text-yellow-900 mb-3">
                          <i className="ri-lightbulb-line mr-2"></i>
                          重要提醒
                        </h5>
                        <p className="text-yellow-800">
                          始终强调"风险转移点"和"费用划分点"是两个不同的概念。理解这一点对于正确选择贸易术语至关重要。
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Section 3: 关键单证简介 */}
                  {section.id === 3 && (
                    <div>
                      <p className="text-lg text-gray-700 mb-8">{section.content.description}</p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {section.content.documents.map((doc, docIndex) => (
                          <div key={docIndex} className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-shadow">
                            <div className="flex items-start space-x-4">
                              <div className={`w-12 h-12 bg-gradient-to-r ${doc.color} rounded-lg flex items-center justify-center flex-shrink-0`}>
                                <i className={`${doc.icon} text-white text-xl`}></i>
                              </div>
                              <div className="flex-1">
                                <h4 className="text-xl font-bold text-gray-900 mb-1">{doc.name}</h4>
                                <p className="text-gray-600 text-sm mb-3">{doc.englishName}</p>
                                <p className="text-gray-700 mb-4">{doc.description}</p>
                                
                                <div className="mb-4">
                                  <h5 className="font-semibold text-gray-900 mb-2">主要内容：</h5>
                                  <ul className="space-y-1">
                                    {doc.contents.map((content, idx) => (
                                      <li key={idx} className="flex items-center text-sm text-gray-700">
                                        <i className="ri-arrow-right-s-line mr-1 text-gray-400"></i>
                                        {content}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                                
                                <div className={`bg-gradient-to-r ${doc.color} bg-opacity-10 rounded-lg p-3`}>
                                  <h5 className="font-semibold text-gray-900 mb-1">重要性：</h5>
                                  <p className="text-sm text-gray-700">{doc.importance}</p>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Section 4: 付款方式与风险 */}
                  {section.id === 4 && (
                    <div>
                      <p className="text-lg text-gray-700 mb-8">{section.content.description}</p>
                      <div className="space-y-8">
                        {section.content.methods.map((method, methodIndex) => (
                          <div key={methodIndex} className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                            <div className={`bg-gradient-to-r ${method.color} p-6 text-white`}>
                              <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-4">
                                  <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                                    <i className={`${method.icon} text-white text-xl`}></i>
                                  </div>
                                  <div>
                                    <h4 className="text-2xl font-bold mb-1">{method.name}</h4>
                                    <p className="text-white/80">{method.englishName}</p>
                                  </div>
                                </div>
                                <span className="bg-white/20 text-white px-3 py-1 rounded-full text-sm font-medium">
                                  {method.popularity}
                                </span>
                              </div>
                            </div>
                            
                            <div className="p-6">
                              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                <div>
                                  <h5 className="font-semibold text-gray-900 mb-3">
                                    <i className="ri-flow-chart mr-2"></i>
                                    操作流程
                                  </h5>
                                  <ol className="space-y-2">
                                    {method.process.map((step, idx) => (
                                      <li key={idx} className="flex items-start text-sm text-gray-700">
                                        <span className="w-5 h-5 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs font-bold mr-3 mt-0.5 flex-shrink-0">
                                          {idx + 1}
                                        </span>
                                        {step}
                                      </li>
                                    ))}
                                  </ol>
                                </div>
                                
                                <div className="space-y-4">
                                  <div className="bg-green-50 rounded-lg p-4">
                                    <h5 className="font-semibold text-green-900 mb-2">
                                      <i className="ri-thumb-up-line mr-2"></i>
                                      优势
                                    </h5>
                                    <ul className="space-y-1">
                                      {method.advantages.map((adv, idx) => (
                                        <li key={idx} className="flex items-center text-sm text-green-800">
                                          <i className="ri-check-line mr-2 text-green-600"></i>
                                          {adv}
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                  
                                  <div className="bg-red-50 rounded-lg p-4">
                                    <h5 className="font-semibold text-red-900 mb-2">
                                      <i className="ri-alert-line mr-2"></i>
                                      风险提示
                                    </h5>
                                    <ul className="space-y-1">
                                      {method.risks.map((risk, idx) => (
                                        <li key={idx} className="flex items-start text-sm text-red-800">
                                          <i className="ri-error-warning-line mr-2 text-red-600 mt-0.5 flex-shrink-0"></i>
                                          {risk}
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                </div>
                              </div>
                              
                              <div className="mt-4 bg-blue-50 rounded-lg p-4">
                                <h5 className="font-semibold text-blue-900 mb-2">
                                  <i className="ri-lightbulb-line mr-2"></i>
                                  专家建议
                                </h5>
                                <p className="text-sm text-blue-800">{method.recommendation}</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 py-16">
        <div className="max-w-4xl mx-auto text-center px-6">
          <h2 className="text-4xl font-bold text-white mb-4">
            开始您的外贸学习之旅
          </h2>
          <p className="text-xl text-blue-100 mb-8 leading-relaxed">
            通过系统学习，掌握外贸核心技能，成为专业的外贸从业者
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <button className="px-8 py-4 bg-white text-blue-600 rounded-lg hover:bg-gray-50 transition-colors font-semibold whitespace-nowrap cursor-pointer">
              <i className="ri-user-add-line mr-2"></i>
              立即报名学习
            </button>
            <button className="px-8 py-4 bg-white/10 text-white rounded-lg hover:bg-white/20 transition-colors backdrop-blur-sm font-semibold whitespace-nowrap cursor-pointer">
              <i className="ri-download-line mr-2"></i>
              下载学习资料
            </button>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                  <i className="ri-graduation-cap-line text-white"></i>
                </div>
                <h3 className="text-xl font-bold" style={{ fontFamily: "Pacifico, serif" }}>天健商业广场外贸培训学院</h3>
              </div>
              <p className="text-gray-400 mb-4">专业的外贸知识培训平台，助您成为外贸专家</p>
              <div className="flex space-x-4">
                <i className="ri-wechat-line text-xl text-gray-400 hover:text-white cursor-pointer transition-colors"></i>
                <i className="ri-qq-line text-xl text-gray-400 hover:text-white cursor-pointer transition-colors"></i>
                <i className="ri-mail-line text-xl text-gray-400 hover:text-white cursor-pointer transition-colors"></i>
              </div>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">课程内容</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">外贸基础入门</a></li>
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">一般贸易解析</a></li>
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">市场采购贸易</a></li>
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">风险防控</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">学习支持</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">在线答疑</a></li>
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">实战案例</a></li>
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">学习资料</a></li>
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">证书认证</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">快速链接</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">关于我们</a></li>
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">联系方式</a></li>
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">服务条款</a></li>
                <li><a href="https://readdy.ai/?origin=logo" className="hover:text-white transition-colors cursor-pointer">Made with Readdy</a></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2024 天健商业广场外贸培训学院. 保留所有权利.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
